import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import {
  getNotifications,
  markNotificationAsRead,
  markAllNotificationsAsRead,
  deleteNotifications,
  deleteAllNonSystemNotifications,
  notifyNotificationsUpdated,
} from '../services/notificationsService';
import { normalizeNotification } from '../utils/notificationHelpers';

const useNotificationInbox = (role) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selected, setSelected] = useState(null);
  const [activeTab, setActiveTab] = useState('all');
  const [filterOpen, setFilterOpen] = useState(false);
  const [filterValue, setFilterValue] = useState('all');
  const [busy, setBusy] = useState(false);
  const [selectMode, setSelectMode] = useState(false);
  const [checkedIds, setCheckedIds] = useState(() => new Set());
  const [deletePrompt, setDeletePrompt] = useState(null);
  const userIdRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        setError('Not signed in');
        return;
      }
      const user = JSON.parse(userStr);
      userIdRef.current = user.id;
      const res = await getNotifications(user.id);
      const list = (Array.isArray(res) ? res : res?.data || []).map((n) =>
        normalizeNotification(n, role),
      );
      setItems(list);
      setError(null);
      setSelected((prev) => {
        if (prev) return list.find((n) => n.id === prev.id) || list[0] || null;
        return list[0] || null;
      });
    } catch {
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [role]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(
    () =>
      items.filter((n) => {
        const tab = filterValue !== 'all' ? filterValue : activeTab;
        if (tab === 'unread') return !n.isRead;
        if (tab === 'all') return true;
        return n.category === tab;
      }),
    [items, filterValue, activeTab],
  );

  const removableIds = useMemo(
    () => new Set(items.filter((n) => !n.isSystem).map((n) => n.id)),
    [items],
  );

  const toggleSelectMode = () => {
    setSelectMode((on) => {
      if (on) setCheckedIds(new Set());
      return !on;
    });
  };

  const toggleChecked = (id) => {
    setCheckedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const removeFromState = (idsToRemove) => {
    const idSet = new Set(idsToRemove);
    setItems((prev) => {
      const next = prev.filter((n) => !idSet.has(n.id));
      setSelected((current) => (current && idSet.has(current.id) ? next[0] || null : current));
      return next;
    });
    setCheckedIds((prev) => {
      const next = new Set(prev);
      idsToRemove.forEach((id) => next.delete(id));
      return next;
    });
  };

  const requestDeleteBulk = () => {
    if (selectMode) {
      const ids = [...checkedIds].filter((id) => removableIds.has(id));
      if (!ids.length) return;
      setDeletePrompt({
        title: 'Delete selected notifications?',
        message: `Delete ${ids.length} selected notification${ids.length === 1 ? '' : 's'}? This cannot be undone.`,
        mode: 'selected',
        ids,
      });
      return;
    }

    if (!items.some((n) => !n.isSystem)) return;
    setDeletePrompt({
      title: 'Delete all notifications?',
      message:
        'Delete all notifications except Tutor Scene system messages? This cannot be undone.',
      mode: 'all',
      ids: [],
    });
  };

  const performDelete = async () => {
    if (!userIdRef.current || !deletePrompt) return;
    setBusy(true);
    try {
      if (deletePrompt.mode === 'all') {
        await deleteAllNonSystemNotifications(userIdRef.current);
        const remaining = items.filter((n) => n.isSystem);
        setItems(remaining);
        setSelected(remaining[0] || null);
        setCheckedIds(new Set());
        setSelectMode(false);
      } else {
        const ids = deletePrompt.ids;
        if (!ids.length) return;
        await deleteNotifications(userIdRef.current, ids);
        removeFromState(ids);
        if (selectMode && checkedIds.size === ids.length) {
          setSelectMode(false);
          setCheckedIds(new Set());
        }
      }
      notifyNotificationsUpdated();
      setDeletePrompt(null);
    } catch {
      setError('Could not delete notification(s)');
    } finally {
      setBusy(false);
    }
  };

  const requestDeleteOne = (item) => {
    if (item.isSystem) return;
    setDeletePrompt({
      title: 'Delete notification?',
      message: 'This notification will be permanently removed.',
      mode: 'selected',
      ids: [item.id],
    });
  };

  const markItemRead = async (item) => {
    if (!item || item.isRead || !userIdRef.current) return item;
    await markNotificationAsRead(userIdRef.current, item.id);
    const updated = { ...item, isRead: true };
    setItems((prev) => prev.map((n) => (n.id === item.id ? updated : n)));
    setSelected((prev) => (prev?.id === item.id ? updated : prev));
    notifyNotificationsUpdated();
    return updated;
  };

  const handleSelect = async (item) => {
    if (selectMode) {
      if (!item.isSystem) toggleChecked(item.id);
      return;
    }
    setSelected(item);
    await markItemRead(item);
  };

  const handleMarkAllRead = async () => {
    if (!userIdRef.current) return;
    setBusy(true);
    await markAllNotificationsAsRead(userIdRef.current);
    setItems((prev) => prev.map((n) => ({ ...n, isRead: true })));
    setSelected((prev) => (prev ? { ...prev, isRead: true } : prev));
    notifyNotificationsUpdated();
    setBusy(false);
  };

  return {
    items,
    loading,
    error,
    selected,
    setSelected,
    activeTab,
    setActiveTab,
    filterOpen,
    setFilterOpen,
    filterValue,
    setFilterValue,
    busy,
    selectMode,
    checkedIds,
    deletePrompt,
    filtered,
    load,
    toggleSelectMode,
    toggleChecked,
    requestDeleteOne,
    requestDeleteBulk,
    performDelete,
    cancelDelete: () => setDeletePrompt(null),
    handleSelect,
    handleMarkAllRead,
    markItemRead,
    userIdRef,
  };
};

export default useNotificationInbox;
